// appels/_shared types: the appel as the API returns it (appels-v1.md), checked against the live API
// on 4 Nov 2026. Zod schemas are the contract, parsed at the service boundary.
import { z } from "zod";

const TextPairSchema = z.object({ fr: z.string(), en: z.string() });

export const STEP_KEYS = [
  "PROGRAMME",
  "ELIGIBILITY",
  "SCORING_GRID",
  "DOCUMENTS_MESSAGES",
  "SUPPORT",
] as const;

export type StepKey = (typeof STEP_KEYS)[number];

export const STEP_STATES = ["COMPLETED", "IN_PROGRESS", "LOCKED"] as const;

export const AppelSchema = z.object({
  id: z.string(),
  /** Sent back as `expected_revision` on every save. */
  revision: z.number(),
  cohort_id: z.string(),
  /** Generated by the server (`APL-COH-000009-001`), read-only. */
  code: z.string(),
  name: z.string(),
  /** `DRAFT` is the only status in this workflow; kept open for the next ones. */
  status: z.string(),
  opening_at: z.string(),
  closing_at: z.string(),
  presentation: TextPairSchema,
  rhythm: TextPairSchema,
  location: TextPairSchema,
  steps: z.array(
    z.object({
      key: z.string(),
      state: z.enum(STEP_STATES).catch("LOCKED"),
    })
  ),
  /** Read-only facts copied from the cohort; changed in the cohort's settings. */
  cohort: z.object({
    id: z.string(),
    code: z.string(),
    name: z.string(),
    places: z.number(),
    waitlist_places: z.number(),
    interview_margin_percent: z.number().nullable(),
    response_delay_days: z.number().nullable(),
    program_start_date: z.string().nullable(),
    program_end_date: z.string().nullable(),
  }),
  /** « Soit 30 entretiens »: worked out by the server, `null` while the margin is not set. */
  interviews_count: z.number().nullable(),
  /** The day the foundation answers the applicants (date, Algeria time); `null` while not set. */
  answers_date: z.string().nullable(),
  available_actions: z.record(z.string(), z.boolean()),
});

export type Appel = z.infer<typeof AppelSchema>;

/** The state of one step, `LOCKED` when the API does not list it. */
export function stepState(
  appel: Appel,
  key: StepKey
): "COMPLETED" | "IN_PROGRESS" | "LOCKED" {
  return appel.steps.find((step) => step.key === key)?.state ?? "LOCKED";
}
